import HBIcon from "@/components/HBIcon";
import Image from "@/components/Image";
import { PIN_PRIVATE_STATUS } from "@/constants/pin";
import displayNum from "@/utils/displayNum";
import getTimeDuration from "@/utils/getTimeDuration";
import imageUrl from "@/utils/imageUrl";
import classNames from "classnames";
import React, { useCallback, useMemo } from "react";
import { When } from "react-if";
import styles from "./index.module.less";

interface BoardCardNewProps {
  record: any;
  className?: string;
  hideButton?: boolean;
  hideUser?: boolean;
  style?: React.CSSProperties;
  noClick?: boolean;
  disableHoverMask?: boolean;
  location?: number; // 新增：位置索引
  viewSource?: string | number; // 新增：viewSource参数
  searchWord?: string;
  stackIndex?: number; // 新增：弹窗堆栈索引，用于生成唯一key
  isCurrentBoard?: boolean; // 新增：是否是当前采集所在画板
}

/**
 * 新的画板卡片组件 - 宽度固定244px
 */
function BoardCardNew({
  className,
  record,
  hideButton = false,
  hideUser = false,
  style,
  noClick,
  disableHoverMask,
  location,
  viewSource,
  searchWord,
  stackIndex = 0,
  isCurrentBoard = false,
  ...rest
}: BoardCardNewProps) {
  const board = record;

  // 生成带有viewSource参数的URL
  const getBoardUrl = useCallback(() => {
    const baseUrl = `/boards/${board?.board_id}`;
    if (viewSource) {
      return `${baseUrl}?viewSource=${viewSource}`;
    }
    return baseUrl;
  }, [board?.board_id, viewSource]);

  // 获取画板图片
  const imgs = useMemo(() => {
    if (!board?.pins) {
      return [];
    }
    return board.pins
      .slice(0, 3)
      .filter(
        (pin) => pin?.file && pin.is_private !== PIN_PRIVATE_STATUS.VIOLATION
      )
      .map((pin) => pin.file);
  }, [board]);

  // 更新时间
  const updateTime = useMemo(() => {
    const createTime = record?.pins?.[0]?.created_at;
    if (createTime) {
      return getTimeDuration({
        time: createTime * 1000,
        maxShowTime: [1, "year"],
        customTimeMap: [
          ["M", "月"],
          ["d", "天"],
          ["h", "小时"],
          ["m", "分钟"],
        ],
      });
    }
    return false;
  }, [record]);

  const onImgLoad = useCallback((evt) => {
    const target = evt.target;
    target?.classList.add("transparent-img-bg");
  }, []);

  // 私密画板判断
  const isPrivate = useMemo(() => {
    return (
      board?.is_private && board?.is_private !== 0 && board?.is_private !== 1
    );
  }, [board]);

  // 渲染图片网格：始终输出三格框架，缺失图片处渲染占位节点
  const renderImages = () => {
    const firstImg = imgs[0] || null;
    const otherImgs = imgs.slice(1, 3); // 最多两张右侧小图

    // 构造右侧两个 slot，无图时为 null
    const rightSlots = [otherImgs[0] || null, otherImgs[1] || null];

    return (
      <div className={styles.imgGrid}>
        {/* 第一张（或占位） */}
        {firstImg ? (
          <div
            className={classNames(styles.imgItem, styles.firstImg)}
            style={{
              backgroundColor:
                firstImg.theme && `#${firstImg.theme.replace("#", "")}`,
            }}
          >
            <Image
              className={classNames("hb-image", "transparent-img-bg")}
              alt={board.title}
              src={imageUrl(firstImg, { width: 163 })}
              srcSet={`${imageUrl(firstImg, { width: 326 })} 2x`}
              width={163}
              height={163}
              loading="lazy"
              onLoad={onImgLoad}
              showError={true}
            />
          </div>
        ) : (
          <div
            className={classNames(
              styles.imgItem,
              styles.firstImg,
              styles.emptyImg
            )}
          />
        )}

        {/* 右侧小图容器：始终渲染两个格子（占位或图片） */}
        <div className={styles.rightImages}>
          {rightSlots.map((img, index) =>
            img ? (
              <div
                key={`${stackIndex}-${board.board_id}-${img.key || index + 1}`}
                className={classNames(styles.imgItem, styles.otherImg)}
                style={{
                  backgroundColor:
                    img.theme && `#${img.theme.replace("#", "")}`,
                }}
              >
                <Image
                  className={classNames("hb-image", "transparent-img-bg")}
                  alt={board.title}
                  src={imageUrl(img, { width: 80 })}
                  srcSet={`${imageUrl(img, { width: 160 })} 2x`}
                  width={80}
                  height={80}
                  loading="lazy"
                  onLoad={onImgLoad}
                  showError
                />
              </div>
            ) : (
              <div
                key={`${stackIndex}-${board.board_id}-empty-${index}`}
                className={classNames(
                  styles.imgItem,
                  styles.otherImg,
                  styles.emptyImg
                )}
              />
            )
          )}
        </div>
      </div>
    );
  };

  return (
    <div
      {...rest}
      className={classNames(styles.boardCard, className, "board-card-new", {
        [styles.disabledHoverMask]: disableHoverMask,
      })}
      style={style}
      data-dist-code={record?.custom_recommend ? "人工" : "算法"}
      data-board_location={location}
    >
      {/* 图片区域 */}
      <div className={styles.album}>
        <a
          href={getBoardUrl()}
          target="_blank"
          className={styles.imgWrapper}
          data-gd-expose="board_expose"
          data-gd-click="board_click"
          data-board-id={board.board_id}
          data-board_location={location}
          data-search-word={searchWord}
          {...(noClick
            ? {
                onClick(e) {
                  e.preventDefault();
                },
              }
            : {})}
        >
          {renderImages()}

          {/* 所在画板标签 */}
          <When condition={isCurrentBoard}>
            <div className={styles.currentBoardTag}>所在画板</div>
          </When>
        </a>
      </div>

      {/* 标题 */}
      <div className={styles.header}>
        <a
          href={getBoardUrl()}
          target="_blank"
          title={board.title}
          data-gd-expose="board_expose"
          data-gd-click="board_click"
          data-board-id={board.board_id}
          data-board_location={location}
          data-search-word={searchWord}
          {...(noClick
            ? {
                onClick(e) {
                  e.preventDefault();
                },
              }
            : {})}
        >
          {board.title}
        </a>
      </div>

      {/* 信息行：采集数量和更新时间 */}
      <div className={styles.info}>
        <span>{displayNum(board.pin_count)}采集</span>
        {updateTime && <span>{updateTime}更新</span>}
      </div>

      {/* 私密标识 */}
      <When condition={isPrivate}>
        <div className={styles.tag}>
          <HBIcon className={styles.lock} type="ic_lock" />
        </div>
      </When>
    </div>
  );
}

export default React.memo(BoardCardNew);
